import type {
  AllSelectionLists,
  RoleSelectionListKey,
  RoleSelectionLists,
  SelectionListKey,
  SelectionListState,
} from '../types';

/**
 * Validate that selection lists contain all expected keys with non-null values
 */
export const validateSelectionLists = <T extends SelectionListKey>(
  selectionList: AllSelectionLists | unknown
): selectionList is SelectionListState<T> => {
  if (!selectionList || typeof selectionList !== 'object') return false;

  return Object.keys(selectionList).every(
    (key) => (selectionList as AllSelectionLists)[key as T] !== null
  );
};

export interface SanitizeResult<T extends SelectionListKey> {
  data: SelectionListState<T> | null;
  strippedKeys: string[];
}

/**
 * Strip null fields from selection lists and return the rest.
 * Returns null data only if every field is null or the input is invalid.
 */
export const sanitizeSelectionLists = <T extends SelectionListKey>(
  selectionList: AllSelectionLists | unknown
): SanitizeResult<T> => {
  if (!selectionList || typeof selectionList !== 'object') {
    return { data: null, strippedKeys: [] };
  }

  const strippedKeys: string[] = [];
  const cleaned = Object.entries(
    selectionList as Record<string, unknown>
  ).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value === null || value === undefined) {
      strippedKeys.push(key);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

  if (strippedKeys.length > 0) {
    console.warn(
      `[sanitizeSelectionLists] Null fields stripped from API response: ${strippedKeys.join(', ')}. Filters backed by these fields will be unavailable.`
    );
  }

  const data =
    Object.keys(cleaned).length > 0 ? (cleaned as SelectionListState<T>) : null;

  return { data, strippedKeys };
};

/**
 * Validate role selection lists have expected structure
 */
export const validateRoleSelectionLists = (
  selectionListState: RoleSelectionLists | null
): selectionListState is SelectionListState<RoleSelectionListKey> => {
  if (!selectionListState) return false;

  // Check that at least some role lists are present
  const hasRoleLists = Object.keys(selectionListState).some(
    (key) =>
      key.startsWith('role_k') &&
      selectionListState[key as keyof RoleSelectionLists] !== null
  );

  return hasRoleLists;
};

/**
 * Check if a selection list is role-related
 */
export const isRoleSelectionList = (listId: string): boolean => {
  const roleListIds = [
    'job_category',
    'role_k7',
    'role_k50',
    'role_k150',
    'role_k300',
    'role_k500',
    'role_k1000',
    'role_k1250',
    'role_k1500',
    'role_k5000',
    'role_k15000',
  ];

  return roleListIds.includes(listId);
};
